import UploadFileToFolder from '@/components/common/UploadFileToFolder';
import ThesaurusDownloadButton from '@/components/Thesaurus/buttons/ThesaurusDownloadButton';
import { validateThesaurusImportFile } from '@/components/Thesaurus/thesaurusImportValidator';
import ThesaurusLink from '@/components/Thesaurus/ThesaurusLink';
import ThesaurusSearch from '@/components/Thesaurus/ThesaurusSearch';
import { useGetThesaurusQuery } from '@/features/api/endpoints/thesaurusEndpoints';
import { formatNumber } from '@/shared/utils/number-utils';
import { FILE_TYPES } from '@/shared/utils/upload-utils';
import {
  thesaurusSortColumns,
  type ThesaurusRow,
  type ThesaurusSortColumn,
} from '@/models/thesaurus.type';
import { Group, Stack, Text, Title } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';

const RECORDS_PER_PAGE_OPTIONS = [25, 50, 100];
const DATE_FORMAT = 'L';
const EMPTY_VALUE = '—';

const DEFAULT_SORT: DataTableSortStatus<ThesaurusRow> = {
  columnAccessor: 'nom',
  direction: 'asc',
};

const isThesaurusSortColumn = (value: unknown): value is ThesaurusSortColumn =>
  thesaurusSortColumns.includes(value as ThesaurusSortColumn);

const formatDate = (value: string | null): string =>
  value ? dayjs(value).format(DATE_FORMAT) : EMPTY_VALUE;

/** Valeur de tri d'une ligne : nombres convertis, chaînes insensibles à la casse. */
const sortValue = (row: ThesaurusRow, column: ThesaurusSortColumn): number | string => {
  const value = row[column];
  if (value == null) return '';
  if (column === 'nb_valeurs') return Number(value);
  return typeof value === 'string' ? value.toLocaleLowerCase('fr-FR') : value;
};

const matchesSearch = (row: ThesaurusRow, search: string): boolean =>
  [row.nom, row.description].some(value => value?.toLocaleLowerCase('fr-FR').includes(search));

const ThesaurusPage = () => {
  const { t } = useTranslate();
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<ThesaurusRow>>(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(RECORDS_PER_PAGE_OPTIONS[0]);

  useDocumentTitle(t('admin.thesaurus.title'));

  const { data, isError, isFetching } = useGetThesaurusQuery();

  const sortedRecords = useMemo(() => {
    const normalizedSearch = search.toLocaleLowerCase('fr-FR');
    const filtered = normalizedSearch
      ? (data ?? []).filter(row => matchesSearch(row, normalizedSearch))
      : (data ?? []);
    const { columnAccessor, direction } = sortStatus;
    if (!isThesaurusSortColumn(columnAccessor)) return filtered;
    const sorted = sortBy(filtered, row => sortValue(row, columnAccessor));
    return direction === 'desc' ? sorted.reverse() : sorted;
  }, [data, search, sortStatus]);

  // Pagination côté client : tous les thésaurus sont chargés en une seule requête.
  const records = useMemo(
    () => sortedRecords.slice((page - 1) * recordsPerPage, page * recordsPerPage),
    [sortedRecords, page, recordsPerPage],
  );

  // Toute modification du tri, de la recherche ou de la taille de page ramène à la première page.
  const handleSortStatusChange = (status: DataTableSortStatus<ThesaurusRow>) => {
    setSortStatus(status);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setPage(1);
  };

  const columns = useMemo(() => {
    const title = (accessor: string): string => t(`admin.thesaurus.columns.${accessor}`);

    return [
      { accessor: 'nom', title: title('nom'), sortable: true },
      {
        accessor: 'description',
        title: title('description'),
        render: ({ description }) => description ?? EMPTY_VALUE,
      },
      {
        accessor: 'version',
        title: title('version'),
        sortable: true,
        textAlign: 'center',
        render: ({ version }) => version ?? EMPTY_VALUE,
      },
      {
        accessor: 'nb_valeurs',
        title: title('nb_valeurs'),
        sortable: true,
        textAlign: 'right',
        render: ({ nb_valeurs }) => formatNumber(nb_valeurs),
      },
      {
        accessor: 'date_heure_debut_validite',
        title: title('date_heure_debut_validite'),
        sortable: true,
        textAlign: 'center',
        render: ({ date_heure_debut_validite }) => formatDate(date_heure_debut_validite),
      },
      {
        accessor: 'liens',
        title: title('liens'),
        render: ({ documentation_url, source_url }) =>
          documentation_url?.trim() || source_url?.trim() ? (
            <Stack gap={4}>
              <ThesaurusLink
                href={documentation_url}
                label={t('admin.thesaurus.links.documentation')}
              />
              <ThesaurusLink href={source_url} label={t('admin.thesaurus.links.source')} />
            </Stack>
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        accessor: 'actions',
        title: '',
        textAlign: 'center',
        pinned: 'right',
        render: ({ id, nom, version }) => (
          <Group gap={4} wrap="nowrap" justify="center">
            {version && <ThesaurusDownloadButton id={id} nom={nom} version={version.toString()} />}
          </Group>
        ),
      },
    ] satisfies DataTableColumn<ThesaurusRow>[];
  }, [t]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" gap="sm">
        <Title order={1}>{t('admin.thesaurus.title')}</Title>
        <Group gap="sm" wrap="nowrap">
          <ThesaurusSearch onSearch={handleSearchChange} />
          <UploadFileToFolder
            folder={import.meta.env.VITE_DIRECTUS_THESAURUS_UPLOAD_FOLDER}
            label={t('admin.thesaurus.upload')}
            accept={FILE_TYPES.csv}
            maxSize={10 * 1024 * 1024}
            validate={validateThesaurusImportFile}
          />
        </Group>
      </Group>

      {isError ? (
        <Text c="red" role="alert">
          {t('admin.thesaurus.error')}
        </Text>
      ) : (
        <DataTable
          withTableBorder
          withColumnBorders
          minHeight={200}
          fetching={isFetching}
          records={records}
          idAccessor="id"
          columns={columns}
          noRecordsText={t('admin.thesaurus.empty')}
          sortStatus={sortStatus}
          onSortStatusChange={handleSortStatusChange}
          page={page}
          onPageChange={setPage}
          totalRecords={sortedRecords.length}
          recordsPerPage={recordsPerPage}
          recordsPerPageOptions={RECORDS_PER_PAGE_OPTIONS}
          onRecordsPerPageChange={handleRecordsPerPageChange}
          recordsPerPageLabel={t('admin.thesaurus.recordsPerPage')}
          paginationWithEdges
          paginationText={({ from, to, totalRecords: total }) =>
            t('admin.thesaurus.pagination', { from, to, total })
          }
        />
      )}
    </Stack>
  );
};

export default ThesaurusPage;
